import React from "react";
import ReactDOM from "react-dom/client";
import RpaTransport from "./RpaTransport";
import _ from "lodash";
import RpaPeopleReservation from "./RpaPeopleReservation";
import RpaLocation from "./RpaLocation";
import RpaOption from "./RpaOption";
import TicketControlLogic from "../../../app/modules/ticket/logics/TicketControlLogic";

/** @typedef {import('../models/RpaLocationModel.js').RpaLocationItem } RpaLocationItem */
/** @typedef {import('../models/RpaResourceModel.js').RpaResourceItem } RpaResourceItem */
/** @typedef {import('../models/RpaOptionModel.js').RpaOptionItem } RpaOptionItem */
/** @typedef {import('../models/RpaParkingModel.js').RpaParkingItem } RpaParkingItem */

/**
 * @typedef {object} RpaReservationProps
 * @property {number} rpaResourceId - Identifiant de la ressource
 * @property {?RpaResourceItem} rpaResource - Ressource RPA associée
 * @property {string} rpaReservationStartHour - Heure de début de la réservation
 * @property {string} rpaReservationEndHour - Heure de fin de la réservation
 * @property {string} day - Jour de la réservation
 * @property {0|1} priceType - Type de la tarification, heures pleines 0 et heures creuses 1
 * @property {boolean} chooseTransport - Indique si l'utilisateur doit choisir un transport
 * @property {boolean} chooseLocation - Indique si l'utilisateur doit choisir une location
 * @property {boolean} choosePeopleReservations - Indique si l'utilisateur doit choisir des personnes supplémentaires
 * @property {number} nbPeoplesMin - Nombre minimum de personnes que l'utilisateur peut renseigner pour cette resource si la propriété choosePeopleReservations est à true ou si une option avec choose_nb_peoples est sélectionnée
 * @property {number} nbPeoplesMax - Nombre maximum de personnes que l'utilisateur peut renseigner pour cette resource si la propriété choosePeopleReservations est à true ou si une option avec choose_nb_peoples est sélectionnée
 * @property {boolean} chooseOption - Indique si l'utilisateur doit choisir des options
 * @property {boolean} chooseTicket - Indique si l'utilisateur peut saisir le numéro unique d'un e-ticket pour sa réservation
 * @property {string} ticketUnicNumber - Numéro unique de l'e-ticket saisi
 * @property {Object.<string, number>} totalPeoplesObj - Objet de la répartition du nombre de personnes par règle de service
 * @property {number} nbTotalPeoples - Nombre total de personnes
 * @property {Array<RpaLocationItem>} rpaLocations - Liste des locations disponibles
 * @property {Array<RpaLocationItem>} targetedRpaLocations - Liste des locations ciblées pour le nombre total de personnes
 * @property {Array<RpaOptionItem>} rpaOptions - Liste des options disponibles pour cette resource
 * @property {?RpaLocationItem} selectedRpaParking - Stationnement sélectionné
 */

class RpaReservation extends React.Component {
  /**
   * Constructeur du composant RpaReservation
   * @param {any} props - Propriétés du composant
   */
  constructor(props) {
    super(props);

    this.state = /** @type {RpaReservationProps} */ ({
      rpaResourceId: parseInt(props.rpaResourceId, 10),
      rpaResource: null,
      rpaReservationStartHour: props.rpaReservationStartHour,
      rpaReservationEndHour: props.rpaReservationEndHour,
      day: props.day,
      priceType: parseInt(props.priceType, 10),
      chooseTransport: props.chooseTransport === "true",
      chooseLocation: props.chooseLocation === "true",
      chooseOption: props.chooseOption === "true",
      chooseTicket: props.chooseTicket === "true",
      ticketUnicNumber: "",
      choosePeopleReservations: props.choosePeopleReservations === "true",
      nbPeoplesMin: 1,
      nbPeoplesMax: 1,
      totalPeoplesObj: {
        nb_adult: 1
      },
      nbTotalPeoples: 1,
      rpaLocations: [],
      targetedRpaLocations: [],
      selectedRpaParking: null,
      rpaOptions: []
    });
  }

  async componentDidMount() {
    var rpaLocations = await this._getRpaLocations(),
      rpaOptions = await this._getRpaOptions(),
      rpaResource = await this._getRpaResource(),
      targetedRpaLocations = this._getTargetedRpaLocations(rpaLocations, this.state.nbTotalPeoples),
      stateObj = /** @type {any} */ ({
        rpaLocations,
        rpaOptions,
        rpaResource,
        targetedRpaLocations
      });

    stateObj =
      this.state.chooseOption && rpaOptions && rpaOptions.length
        ? {
            ...stateObj,
            nbPeoplesMin: rpaOptions[0].nb_peoples_min,
            nbPeoplesMax: rpaOptions[0].nb_peoples_max,
            totalPeoplesObj: {
              nb_adult: rpaOptions[0].nb_peoples_min || 1
            }
          }
        : {
            ...stateObj,
            nbPeoplesMin: rpaResource.nb_peoples_min,
            nbPeoplesMax: rpaResource.nb_peoples_max,
            totalPeoplesObj: {
              nb_adult: rpaResource.nb_peoples_min || 1
            }
          };

    this.setState(stateObj, async () => {
      await this.refreshTotalPeoples(this.state.totalPeoplesObj);
    });
  }

  /**
   * Permet de récupérer les locations de la resource
   * @returns {Promise<Array<RpaLocationItem>>} La liste des locations de la resource
   */
  async _getRpaLocations() {
    const apiUrl = `/api/rpa-location/stock/_search?rpaResourceId=${this.state.rpaResourceId}&day=${this.state.day}`,
      response = await fetch(apiUrl, {
        headers: {
          Authorization: "gestiaweb"
        }
      });

    return await response.json();
  }

  /**
   * Permet de récupérer les options de la resource
   * @returns {Promise<Array<RpaOptionItem>>} La liste des options de la resource
   */
  async _getRpaOptions() {
    const apiUrl = `/api/rpa-option/search?rpaResourceId=${this.state.rpaResourceId}`,
      response = await fetch(apiUrl, {
        headers: {
          Authorization: "gestiaweb"
        }
      });

    return await response.json();
  }

  /**
   * Permet de récupérer la resource RPA associée
   * @returns {Promise<RpaResourceItem>} Resource
   */
  async _getRpaResource() {
    const apiUrl = `/api/rpa-resource/search?rpaResourceId=${this.state.rpaResourceId}`,
      response = await fetch(apiUrl, {
        headers: {
          Authorization: "gestiaweb"
        }
      });

    return await response.json();
  }

  /**
   * Permet de trouver les locations ciblées pour un nombre total de personnes
   * @param {Array<RpaLocationItem>} rpaLocations - Liste des locations
   * @param {number} nbTotalPeoples - Nombre de personnes pour les locations
   * @returns {Array<RpaLocationItem>} Liste des locations ciblées
   */
  _getTargetedRpaLocations(rpaLocations, nbTotalPeoples) {
    var findedRpaLocations = /** @type {Array<RpaLocationItem>} */ ([]),
      remaining = nbTotalPeoples,
      clonedRpaLocations = /** @type {Array<RpaLocationItem>} */ ([]),
      bestRpaLocationToChoose = /** @type {RpaLocationItem} */ ({});

    // NOTE s'il n'y a pas de location on retourne un tableau vide
    if (!rpaLocations || !rpaLocations.length) return [];

    // NOTE On clone la liste pour ne pas modifier le state directement
    clonedRpaLocations = /** @type {Array<RpaLocationItem>} */ (rpaLocations.map(rpaLocation => ({ ...rpaLocation })));

    // NOTE On trie les locations du plus grand au plus petit pour optimiser le remplissage
    clonedRpaLocations.sort((a, b) => b.nb_persons - a.nb_persons);

    while (remaining > 0) {
      // NOTE Cherche la meilleure location qui correspond pile-poil au nombre de personnes restantes
      bestRpaLocationToChoose = /** @type {RpaLocationItem} */ (clonedRpaLocations.find(c => c.nb_persons <= remaining && c.real_sock_during_day > 0));

      // NOTE Si aucune location pile-poil n’est trouvée, on prend la plus petite dispo
      if (!bestRpaLocationToChoose) {
        bestRpaLocationToChoose = /** @type {RpaLocationItem} */ (clonedRpaLocations.find(c => c.real_sock_during_day > 0));
      }

      if (!bestRpaLocationToChoose) break; // plus de stock disponible → stop

      // NOTE On ajoute la location sélectionnée
      findedRpaLocations.push(bestRpaLocationToChoose);

      // NOTE On réduit le stock
      bestRpaLocationToChoose.real_sock_during_day--;

      // NOTE On retire le nombre de personnes prises
      remaining -= bestRpaLocationToChoose.nb_persons;
    }

    return findedRpaLocations;
  }

  /**
   * Permet de rafraîchir le total des personnes
   * @param {Object.<string, number>} totalPeoplesObj - Objet de la répartition du nombre de personnes par règle de service
   */
  async refreshTotalPeoples(totalPeoplesObj) {
    var nbTotalPeoples = Object.values(totalPeoplesObj).reduce((a, b) => a + b, 0),
      targetedRpaLocations = this._getTargetedRpaLocations(this.state.rpaLocations, nbTotalPeoples);

    this.setState({
      totalPeoplesObj,
      nbTotalPeoples,
      targetedRpaLocations
    });
  }

  /**
   * Permet de rafraîchir le stationnement sélectionné
   * @param {RpaParkingItem} rpaParking - Stationnement sélectionné
   */
  refreshParking(rpaParking) {
    if (!rpaParking) return;

    // NOTE On vérifie si le stationnement choisi permet de choisir des options sinon on change l'état pour ne pas afficher le choix des options
    const optionsAreDisabled = rpaParking ? rpaParking.options_are_disabled : false;

    this.setState({
      selectedRpaParking: rpaParking,
      chooseOption: optionsAreDisabled ? false : this.state.rpaOptions && this.state.rpaOptions.length > 0
    });
  }

  /**
   * Lors du changement d'option, on regarde s'il ne faut pas changer le choose_nb_peoples en fonction de l'option choisie qui peut aussi avoir ce paramètre
   * @param {RpaOptionItem} rpaOption - Option choisie
   */
  refreshOption(rpaOption) {
    const rpaResource = /** @type {RpaResourceItem} */ (this.state.rpaResource);

    if (rpaOption.choose_nb_peoples) {
      this.setState({
        choosePeopleReservations: true,
        nbPeoplesMin: rpaOption.nb_peoples_min,
        nbPeoplesMax: rpaOption.nb_peoples_max
      });
    } else {
      this.setState({
        choosePeopleReservations: false,
        nbPeoplesMin: rpaResource.nb_peoples_min,
        nbPeoplesMax: rpaResource.nb_peoples_max
      });
    }
  }

  render() {
    return (
      <>
        {this.state.chooseTransport ? (
          <>
            <p className="paragraph paragraph--bold paragraph--h4 paragraph--no-margin">Choix du stationnement</p>
            <div className="space--16" />
            <RpaTransport rpaOptions={this.state.rpaOptions} rpaResourceId={this.state.rpaResourceId} rpaReservationStartHour={this.state.rpaReservationStartHour} rpaReservationEndHour={this.state.rpaReservationEndHour} refreshParking={this.refreshParking.bind(this)} />
            <div className="space--16" />
          </>
        ) : (
          ""
        )}
        {this.state.chooseOption ? (
          <>
            <div className="space--16" />
            <p className="paragraph paragraph--bold paragraph--h4 paragraph--no-margin">Choix de vos options</p>
            <div className="space--16" />
            <RpaOption rpaOptions={this.state.rpaOptions} refreshOption={this.refreshOption.bind(this)} />
            <div className="space--16" />
          </>
        ) : (
          ""
        )}
        {this.state.choosePeopleReservations ? (
          <>
            <div className="space--16" />
            <p className="paragraph paragraph--bold paragraph--h4 paragraph--no-margin">Nombre de personnes</p>
            <div className="space--16" />
            <RpaPeopleReservation nbPeoplesMin={this.state.nbPeoplesMin} nbPeoplesMax={this.state.nbPeoplesMax} rpaResourceId={this.state.rpaResourceId} priceType={this.state.priceType} totalPeoplesObj={this.state.totalPeoplesObj} refreshTotalPeoples={this.refreshTotalPeoples.bind(this)} />
            <input type="hidden" name="nbTotalPeoples" value={this.state.nbTotalPeoples} />
            <div className="space--16" />
            <div className="alert alert--success">
              <span className="material-symbols-outlined">contacts_product</span>
              <div className="alert__content">
                <div className="alert__text">Nombre total de personnes : {this.state.nbTotalPeoples}</div>
              </div>
            </div>
            <div className="space--16" />
          </>
        ) : (
          ""
        )}
        {this.state.chooseLocation && this.state.rpaResource ? (
          <>
            <p className="paragraph paragraph--bold paragraph--h4 paragraph--no-margin">Matériel de location</p>
            <div className="space--16" />
            <RpaLocation targetedRpaLocations={this.state.targetedRpaLocations} rpaLocations={this.state.rpaLocations} autoLocationAssignement={this.state.rpaResource.auto_location_assignement} nbTotalPeoples={this.state.nbTotalPeoples} />
          </>
        ) : (
          ""
        )}
        {this.state.chooseTicket ? (
          <>
            <div className="space--16" />
            <div className="input-group">
              <label htmlFor="ticket" className="input-group__label">
                Identifiant unique de votre e-ticket
              </label>
              <input type="text" id="ticket" name="ticket-unic-number" className="input-group__input" placeholder="Identifiant unique de votre e-ticket" value={this.state.ticketUnicNumber} onChange={event => this.setState({ ticketUnicNumber: TicketControlLogic.formatUnicNumberInput(event.target.value) })} />
            </div>
          </>
        ) : (
          ""
        )}
      </>
    );
  }
}

var rpaReservationNode = document.getElementById("rpa-reservation");
if (rpaReservationNode) {
  var root = ReactDOM.createRoot(rpaReservationNode);
  root.render(<RpaReservation rpaResourceId={rpaReservationNode.getAttribute("data-resourceid")} rpaReservationStartHour={rpaReservationNode.getAttribute("data-starthour")} rpaReservationEndHour={rpaReservationNode.getAttribute("data-endhour")} chooseTransport={rpaReservationNode.getAttribute("data-choosetransport")} chooseLocation={rpaReservationNode.getAttribute("data-chooselocation")} choosePeopleReservations={rpaReservationNode.getAttribute("data-choosepeoplereservations")} priceType={rpaReservationNode.getAttribute("data-pricetype")} day={rpaReservationNode.getAttribute("data-day")} chooseOption={rpaReservationNode.getAttribute("data-chooseoption")} chooseTicket={rpaReservationNode.getAttribute("data-chooseticket")} />);
}
